/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000140 担当者照会
 */
import { Column, Query } from "@material-table/core";
import {
  B000140Find,
  B000140FindRequest,
  B000120EigyoNmr,
  catchRestError,
} from "clients/rest";
import { B000130BumonNm } from "clients/rest/b000/B000130";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { SDialogContext } from "states";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, useFocus } from "states";
import { B000120 } from "./B000120";

// 呼び出し時のプロパティ
export type B000140Props = Partial<{
  eigyoCd: string;
  bumonCd: string;
  // 削除予定区分チェック除外区分
  deljogaiKbn: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  eigyoCd: string;
  eigyoNmr: string;
  bumonCd: string;
  bumonNm: string;
  shainCdFrom: string;
  shainCdTo: string;
  name: string;
  // 削除予定区分チェック除外区分(1の場合、削除予定区分を無視)
  deljogaiKbn: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  shainCd: string;
  shainNm: string;
  shainKana: string;
  bumonNm: string;
  bumonCd: string;
  eigyoCd: string;
  kengenId: string;
  naisenNo: string;
  keitaiNo: string;
  mailaddress: string;
  seibetsuKbn: string;
  seinenYmd: string;
  nyushaYmd: string;
  taishaYmd: string;
  sakujoyoteiKbn: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  eigyoCd: "",
  eigyoNmr: "",
  bumonCd: "",
  bumonNm: "",
  shainCdFrom: "",
  shainCdTo: "",
  name: "",
  deljogaiKbn: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "shainCd", title: "担当者コード", width: "7rem" },
  { field: "shainNm", title: "担当者", width: "12rem" },
  { field: "shainKana", title: "カナ名", width: "12rem" },
  { field: "bumonNm", title: "部門名", width: "10rem" },
  { field: "bumonCd", hidden: true },
  { field: "eigyoCd", hidden: true },
  { field: "kengenId", hidden: true },
  { field: "naisenNo", hidden: true },
  { field: "keitaiNo", hidden: true },
  { field: "mailaddress", hidden: true },
  { field: "seibetsuKbn", hidden: true },
  { field: "seinenYmd", hidden: true },
  { field: "nyushaYmd", hidden: true },
  { field: "taishaYmd", hidden: true },
  { field: "sakujoyoteiKbn", hidden: true },
];

const focusId = "B000140";

// B000140 担当者照会
export const B000140 = (props: B000140Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: B000140Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 営業所コード処理
      if (props.eigyoCd) {
        methods.setValue("eigyoCd", props.eigyoCd);
        await B000120EigyoNmr(methods, navigate, setMsg);
        focus(2);
      }
      // 部門コード処理
      if (props.bumonCd) {
        methods.setValue("bumonCd", props.bumonCd);
        await B000130BumonNm(methods, navigate, setMsg);
        focus(3);
      }
      // 削除予定区分チェック除外区分処理
      if (props.deljogaiKbn) {
        methods.setValue("deljogaiKbn", props.deljogaiKbn);
      }
      // 初回フォーカス先
      if (!props.eigyoCd && !props.bumonCd) {
        focus(1);
      }
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000140FindRequest = {
        eigyoCd: fields.eigyoCd,
        bumonCd: fields.bumonCd,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        name: fields.name,
        // 削除予定区分チェック除外区分
        deljogaiKbn: fields.deljogaiKbn,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["shainCd,asc"],
      };
      // API呼び出し
      B000140Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="担当者照会"
        gridTemplateColumns="7rem 8rem 1rem 8rem 1rem 7rem 6rem 8rem"
      >
        <SLabel>営業所</SLabel>
        <SNumberField
          name="eigyoCd"
          code
          size={6}
          onBlur={() => B000120EigyoNmr(methods, navigate, setMsg)}
          dialog={<B000120 />}
          handleSubmit={(data: any) => {
            methods.setValue("eigyoCd", data.eigyoCd);
            methods.setValue("eigyoNmr", data.eigyoNmr);
          }}
          {...register(1)}
        />
        <STextField name="eigyoNmr" size={40} disabled gridColumn="span 2" />
        <SLabel gridColumn={6}>部門</SLabel>
        <SNumberField
          name="bumonCd"
          code
          size={6}
          onBlur={() => B000130BumonNm(methods, navigate, setMsg)}
          {...register(2)}
        />
        <STextField name="bumonNm" size={40} disabled />
        <SLabel gridColumn={1}>担当者コード</SLabel>
        <SNumberField name="shainCdFrom" code size={7} {...register(3)} />
        <SText>～</SText>
        <SNumberField name="shainCdTo" code size={7} {...register(4)} />
        <SLabel gridColumn={6}>担当者(カナ)</SLabel>
        <STextField name="name" size={40} onBlur={() => {}} {...register(5)} />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          {...register(6)}
          gridRow={3}
          align="right"
          gridColumn={8}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
